/**
 * StreamShop (liveshop / video commerce) integration.
 *
 * Three independent factories, all driven by a single JSON config emitted by
 * `snippets/static.streamshop-widget.liquid` into `window.SimpleStreamshop`:
 *
 *   1. StreamshopCartBridgeFactory  — listens to the StreamShop postMessage
 *      events and mirrors add/remove into the EvolveHub cart, so the theme's
 *      minicart never drifts from what the shopper did inside the player.
 *   2. StreamshopWidgetFactory      — lazy-loads the floating widget button SDK
 *      (`liveshop-sdk-widget-btn.min.js`) after the first user interaction or an
 *      idle timeout, so it never competes with LCP/TBT on first paint.
 *   3. StreamshopLiveFactory        — hydrates `[data-streamshop-live]` iframes
 *      (sections/streamshop-live.liquid) only when they reach the viewport (or
 *      on click, depending on the section setting).
 *
 * Docs: https://developer.streamshop.com.br/docs/{widget,iframe,events}
 *
 * NOTE on payload shapes: the StreamShop event docs list the event names but not
 * the full field schema of `AddToCartDados` / `RemoveFromCartDados`. The resolver
 * below therefore probes every plausible id field and falls back to the product
 * URL (`/products/<handle>.js`) — the same strategy `static.skin-ai.liquid` uses.
 * Set `debug: true` in the theme settings to log raw payloads and calibrate.
 */
(function () {
  var theme = (window.theme = window.theme || {});

  // The snippet and the live section can both emit this asset; a second
  // execution would double every add-to-cart. First one wins.
  if (theme.streamshop) return;

  var SDK_WIDGET_BTN = 'https://assets.streamshop.com.br/sdk/liveshop-sdk-widget-btn.min.js';
  var MESSAGE_ORIGIN = 'STREAMSHOP';

  var config = window.SimpleStreamshop || {};
  var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var requestIdle =
    window.requestIdleCallback ||
    function (callback) {
      return setTimeout(callback, 1);
    };

  // Debug comes from the theme setting, but can also be flipped per-browser
  // without a redeploy: `?streamshop_debug=1` (sticky) or
  // localStorage.setItem('streamshop_debug', '1') / '0' to clear.
  var debug = (function () {
    var stored = null;
    try {
      var param = new URLSearchParams(window.location.search).get('streamshop_debug');
      if (param !== null) localStorage.setItem('streamshop_debug', param === '0' ? '0' : '1');
      stored = localStorage.getItem('streamshop_debug');
    } catch (error) {
      /* private mode / storage blocked — fall back to the theme setting */
    }
    if (stored === '1') return true;
    if (stored === '0') return false;
    return !!config.debug;
  })();

  function log() {
    if (!debug) return;
    console.log.apply(console, ['[streamshop]'].concat([].slice.call(arguments)));
  }

  // ---------------------------------------------------------------------------
  // Cart bridge
  // ---------------------------------------------------------------------------

  function StreamshopCartBridgeFactory(options) {
    var StreamshopCartBridge = class {
      constructor(options) {
        this.options = options || {};
        this.queue = Promise.resolve();
        this.#setEvents();
      }

      #setEvents() {
        window.addEventListener('message', this.#onMessage.bind(this), false);
      }

      #onMessage(event) {
        var data = event && event.data;
        if (!data || data.from !== MESSAGE_ORIGIN) return;

        var name = data.action || data.event || data.name || data.type;
        log('event', name, data);

        if (name === 'addProductToCart') {
          this.#enqueue(() => this.#onAddToCart(this.#payloadOf(data)));
        } else if (name === 'removeFromCart') {
          this.#enqueue(() => this.#onRemoveFromCart(this.#payloadOf(data)));
        }
      }

      // The SDK can fire several cart events in a row (e.g. quantity steppers).
      // Serialising them keeps /cart/add.js and /cart/change.js from racing.
      #enqueue(task) {
        this.queue = this.queue
          .then(() => this.#waitForEvolveCore())
          .then(task)
          .catch((error) => {
            console.warn('[streamshop] cart sync failed:', error && error.message);
          });
        return this.queue;
      }

      // The minicart bundle is deferred, so an event fired early in the page
      // life can land before EvolveCore exists. Poll briefly instead of losing it.
      #waitForEvolveCore(timeout) {
        if (window.EvolveCore && window.EvolveCore.Cart) return Promise.resolve();

        var deadline = Date.now() + (timeout || 15000);
        return new Promise(function (resolve, reject) {
          var poll = setInterval(function () {
            if (window.EvolveCore && window.EvolveCore.Cart) {
              clearInterval(poll);
              resolve();
            } else if (Date.now() > deadline) {
              clearInterval(poll);
              reject(new Error('EvolveCore indisponível'));
            }
          }, 250);
        });
      }

      #payloadOf(data) {
        return data.data || data.payload || data.dados || data.detail || data;
      }

      // Documented shape:
      //   AddToCart      { cartItem: CartItem, addedQuantity: number }
      //   RemoveFromCart { cartItem: CartItem, removedQuantity: number }
      //   CartItem       { product?: Product, selectedItem?: Product, quantity: number }
      //
      // `selectedItem` is the variant the shopper picked and `product` the base
      // product, so merging them (variant wins) keeps the variant's sku/id while
      // still inheriting fields the variant may omit, like `url`.
      //
      // The array/list branches below are tolerated on purpose: the SDK is
      // versioned independently and older/newer builds have shipped flatter
      // payloads. `quantityKey` is the authoritative delta for this event —
      // `cartItem.quantity` is the resulting total, not what changed.
      #itemsOf(payload, quantityKey) {
        if (!payload) return [];

        var carts = payload.cartItem || payload.cartItems || payload.items || payload;
        if (!Array.isArray(carts)) carts = [carts];

        return carts
          .map((cartItem) => {
            if (!cartItem) return null;

            var base = cartItem.product || cartItem.produto || null;
            var selected = cartItem.selectedItem || cartItem.variant || null;
            var product = base || selected ? Object.assign({}, base, selected) : cartItem;

            return { product: product, quantity: this.#quantityOf(cartItem, payload, quantityKey) };
          })
          .filter(Boolean);
      }

      #quantityOf(cartItem, payload, quantityKey) {
        var raw = payload && quantityKey ? payload[quantityKey] : null;
        if (raw === null || raw === undefined) {
          raw = cartItem.quantity || cartItem.qty || cartItem.quantidade;
        }
        var quantity = parseInt(raw, 10);
        return quantity > 0 ? quantity : 1;
      }

      // Shopify variant ids are long numbers; StreamShop's own ids are short or
      // alphanumeric. Rejecting short ids avoids adding a wrong variant.
      #asVariantId(value) {
        if (value === null || value === undefined) return null;
        var raw = String(value).trim();
        var gid = raw.match(/ProductVariant\/(\d+)/);
        var digits = gid ? gid[1] : raw;
        return /^\d{10,}$/.test(digits) ? Number(digits) : null;
      }

      // Resolve via the product URL, matching the variant by SKU when possible.
      // The documented Product interface is
      //   { id: string; productId?: string; sku: string; name: string; price?: number; url?: string }
      // so `sku` is the only field guaranteed to identify the variant, and `id` /
      // `productId` are StreamShop-side ids that may not be Shopify variant ids.
      async #variantFromUrl(url, sku) {
        if (!url) return null;

        var fromQuery = String(url).match(/[?&]variant=(\d+)/);
        if (fromQuery) return Number(fromQuery[1]);

        var handle = String(url).match(/\/products\/([^/?#]+)/);
        if (!handle) return null;

        var response = await fetch(routeRoot + 'products/' + handle[1] + '.js');
        if (!response.ok) return null;

        var product = await response.json();
        var variants = product.variants || [];

        if (sku) {
          var bySku = variants.find((item) => String(item.sku) === String(sku));
          if (bySku) return bySku.id;
        }

        var variant = variants.find((item) => item.available) || variants[0];
        return variant ? variant.id : null;
      }

      async #resolveVariantId(product) {
        if (!product) return null;

        var sku = product.sku;
        var url = product.url || product.productUrl || product.link || product.href;

        // URL first: it carries either ?variant=<id> or a handle we can resolve
        // against the live catalogue. An id field that merely *looks* numeric can
        // be a PRODUCT id, which would silently add the wrong line.
        var fromUrl = await this.#variantFromUrl(url, sku);
        if (fromUrl) return fromUrl;

        // Fallbacks, in decreasing confidence. Only accepts Shopify-shaped
        // variant ids (10+ digits or a gid://…/ProductVariant/<id>).
        var candidates = [
          product.variantId,
          product.variant_id,
          product.variantID,
          product.shopifyVariantId,
          product.externalVariantId,
          product.externalId,
          product.external_id,
          sku,
          product.id,
        ];

        for (var index = 0; index < candidates.length; index += 1) {
          var variantId = this.#asVariantId(candidates[index]);
          if (variantId) return variantId;
        }

        return null;
      }

      async #onAddToCart(payload) {
        var items = this.#itemsOf(payload, 'addedQuantity');

        var variantsToAdd = [];
        for (var index = 0; index < items.length; index += 1) {
          var item = items[index];
          var variantId = await this.#resolveVariantId(item.product);

          log('resolve', { product: item.product, quantity: item.quantity, variantId: variantId });

          if (!variantId) {
            console.warn('[streamshop] variante não resolvida para o produto:', item.product);
            continue;
          }

          variantsToAdd.push({
            id: variantId,
            quantity: item.quantity,
            properties: { _source: 'streamshop' },
          });
        }

        if (!variantsToAdd.length) return null;

        var cart = await EvolveCore.Cart.add(variantsToAdd);

        EvolveCore.Helpers.triggerEvent('Cart:get', EvolveCore.Cart._store.cart);

        // Opens on every add (default). The drawer collides with the floating
        // widget, which StreamshopCartOffsetFactory + the inline styles handle.
        if (this.options.openCartOnAdd) theme.cart.open();

        return cart;
      }

      async #onRemoveFromCart(payload) {
        var items = this.#itemsOf(payload, 'removedQuantity');
        var cart = null;

        for (var index = 0; index < items.length; index += 1) {
          var item = items[index];
          var variantId = await this.#resolveVariantId(item.product);

          log('resolve', { product: item.product, quantity: item.quantity, variantId: variantId });

          if (!variantId) {
            console.warn('[streamshop] variante não resolvida para remoção:', item.product);
            continue;
          }

          cart = await this.#removeVariant(variantId, item.quantity);
        }

        if (!cart) return null;

        // /cart/change.js bypasses EvolveHub, so push the fresh cart back into
        // its store before asking the minicart to re-render.
        if (EvolveCore.Cart._store) EvolveCore.Cart._store.cart = cart;
        EvolveCore.Helpers.triggerEvent('Cart:get', EvolveCore.Cart._store.cart);

        return cart;
      }

      async #removeVariant(variantId, quantity) {
        var current = await fetch(routeRoot + 'cart.js').then((response) => response.json());
        var lineIndex = (current.items || []).findIndex((item) => item.variant_id === variantId);
        if (lineIndex < 0) return null;

        var line = current.items[lineIndex];
        var nextQuantity = Math.max(0, line.quantity - quantity);

        var response = await fetch(routeRoot + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: lineIndex + 1, quantity: nextQuantity }),
        });

        return response.ok ? response.json() : null;
      }
    };

    return new StreamshopCartBridge(options);
  }

  // ---------------------------------------------------------------------------
  // Floating widget button
  // ---------------------------------------------------------------------------

  function StreamshopWidgetFactory(options) {
    var StreamshopWidget = class {
      constructor(options) {
        this.options = options || {};
        this.loaded = false;
        this.#scheduleLoad();
      }

      // Same deferral shape as component.head-scripts.liquid: first interaction
      // wins, with an idle/timeout floor so the button still shows for users who
      // never interact.
      #scheduleLoad() {
        var load = this.#load.bind(this);
        var events = ['click', 'keydown', 'scroll', 'touchstart', 'pointerdown'];

        this.teardown = () => {
          events.forEach((name) => document.removeEventListener(name, load));
        };
        events.forEach((name) => document.addEventListener(name, load, { once: true, passive: true }));

        setTimeout(function () {
          requestIdle(load, { timeout: 2000 });
        }, this.options.delay || 2500);
      }

      #load() {
        if (this.loaded) return;
        this.loaded = true;
        if (this.teardown) this.teardown();

        window.liveshopSdkWidgetButtonOptions = this.options.widget;

        var script = document.createElement('script');
        script.async = true;
        script.src = SDK_WIDGET_BTN;
        script.onload = function () {
          if (typeof window.ss_widget_btn === 'function') {
            window.ss_widget_btn(window.liveshopSdkWidgetButtonOptions);
          }
        };
        script.onerror = function () {
          console.warn('[streamshop] falha ao carregar o SDK do widget');
        };
        document.head.appendChild(script);

        log('widget sdk requested', this.options.widget);
      }
    };

    return new StreamshopWidget(options);
  }

  // ---------------------------------------------------------------------------
  // Live iframe (sections/streamshop-live.liquid)
  // ---------------------------------------------------------------------------

  function StreamshopLiveFactory(container) {
    var StreamshopLive = class {
      constructor(container) {
        this.section = container;
        this.frame = container.querySelector('[data-streamshop-frame]');
        this.trigger = container.querySelector('[data-streamshop-play]');
        if (!this.frame) return;
        this.#init();
      }

      #init() {
        if (this.section.dataset.streamshopLoad === 'click') {
          this.#setEvents();
        } else {
          this.#observe();
        }
      }

      #setEvents() {
        if (!this.trigger) return this.#observe();
        this.trigger.addEventListener('click', this.#hydrate.bind(this), { once: true });
      }

      #observe() {
        if (!('IntersectionObserver' in window)) return this.#hydrate();

        var observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              observer.unobserve(entry.target);
              this.#hydrate();
            });
          },
          { rootMargin: '200px' }
        );

        observer.observe(this.section);
      }

      #hydrate() {
        var src = this.frame.dataset.src;
        if (!src) return;

        this.frame.src = src;
        delete this.frame.dataset.src;
        this.section.classList.add('is-loaded');

        log('live iframe hydrated', src);
      }
    };

    return new StreamshopLive(container);
  }

  // ---------------------------------------------------------------------------
  // Minicart collision
  //
  // The SDK writes the button's box inline (`position:fixed; right:30px;
  // z-index:10000000; transition:.3s ease-out`), so a stylesheet can only beat
  // `right` with !important. `transform` is NOT set inline and the SDK's
  // `streamshopPulseAn` keyframes animate box-shadow only — so transform is free
  // for us, and it inherits the SDK's own transition for a smooth slide.
  //
  // This only flags the state; the offset and the mobile stacking guard live in
  // the inline <style> of static.streamshop-widget.liquid (the widget is global,
  // so those rules cannot sit in a section-emitted bundle).
  // ---------------------------------------------------------------------------

  function StreamshopCartOffsetFactory() {
    var MINICART = '#app-minicart';
    var OPEN_CLASS = 'Minicart__Wrapper--open';
    var CART_FLAG = 'streamshop-cart-open';

    var PLAYER = '#liveshop-sdk-iframe';
    var DOCKED_FLAG = 'streamshop-player-docked';
    var COVERING_FLAG = 'streamshop-player-covering';
    var DRAWER_WIDTH = 400; // .Minicart panel, used only until it has been measured

    var StreamshopCartOffset = class {
      constructor() {
        this.root = document.documentElement;
        this.minicart = null;
        this.player = null;
        this.#init();
      }

      #init() {
        this.#whenPresent(MINICART, (node) => this.#watchMinicart(node));
        // The player iframe is created on open and removed on close, so re-arm
        // the lookup every time it goes away.
        this.#whenPresent(PLAYER, (node) => this.#watchPlayer(node), true);

        // Whether the player overlaps the drawer is a geometry question, so it
        // has to be re-asked when the viewport changes.
        var resizeTimer = null;
        window.addEventListener(
          'resize',
          () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => this.#syncPlayer(), 150);
          },
          { passive: true }
        );
      }

      // Elements here are appended by other scripts (the minicart section, the
      // StreamShop SDK) well after this file runs.
      #whenPresent(selector, onFound, repeat) {
        var existing = document.querySelector(selector);
        if (existing) {
          onFound(existing);
          if (!repeat) return;
        }

        var observer = new MutationObserver(() => {
          var node = document.querySelector(selector);
          if (!node || node === this['_seen_' + selector]) return;
          this['_seen_' + selector] = node;
          if (!repeat) observer.disconnect();
          onFound(node);
        });
        observer.observe(document.body, { childList: true, subtree: true });
      }

      // Watching the class attribute (rather than Cart:show/Cart:hide) keeps the
      // flag true to the DOM no matter which code path opened the drawer.
      #watchMinicart(minicart) {
        this.minicart = minicart;
        var sync = () => {
          var open = minicart.classList.contains(OPEN_CLASS);
          this.root.classList.toggle(CART_FLAG, open);
          log('minicart', open ? 'open' : 'closed');
          // The drawer only has a measurable box once it is open.
          this.#syncPlayer();
        };
        new MutationObserver(sync).observe(minicart, {
          attributes: true,
          attributeFilter: ['class'],
        });
        sync();
      }

      // The player has three states and each needs different treatment:
      //
      //   docked     — right:16px, ~330px wide, NO inline transform. Sits exactly
      //                where the drawer opens, so it is shifted aside.
      //   centred    — inline `transform: translate(-50%,-50%)`, still narrow.
      //                Sits well clear of the drawer, so it is left alone.
      //   fullscreen — same inline transform, but spans the viewport and buries
      //                the drawer. Shifting it would be nonsense, so it yields
      //                z-index instead and the drawer paints over it.
      //
      // Centred and fullscreen are indistinguishable by inline style (only the
      // rendered width differs), so the covering test is geometric: does the
      // player actually reach into the drawer?
      //
      // The iframe sorts its own offset out through the cascade (its inline
      // transform outranks our stylesheet), but #liveshop-sdk-close-btn never
      // carries one — without DOCKED_FLAG the close/expand buttons would drift
      // away from the player in the expanded states.
      #watchPlayer(player) {
        this.player = player;

        new MutationObserver(() => this.#syncPlayer()).observe(player, {
          attributes: true,
          attributeFilter: ['style'],
        });

        // Drop the flags when the SDK tears the player down.
        var removal = new MutationObserver(() => {
          if (document.body.contains(player)) return;
          removal.disconnect();
          if (this.player === player) this.player = null;
          this.#syncPlayer();
          log('player', 'removed');
        });
        removal.observe(document.body, { childList: true });

        this.#syncPlayer();
      }

      #syncPlayer() {
        var player = this.player;
        var present = !!player && document.body.contains(player);

        var docked = present && !player.style.transform;
        var covering = present && !docked && this.#reachesDrawer(player);

        this.root.classList.toggle(DOCKED_FLAG, docked);
        this.root.classList.toggle(COVERING_FLAG, covering);

        if (present) log('player', docked ? 'docked' : covering ? 'covering' : 'clear');
      }

      // The drawer is flush to the right edge, so its left edge is the viewport
      // minus its width. Measure the panel when we can; it is only laid out once
      // the drawer has been opened at least one time.
      #reachesDrawer(player) {
        var rect = player.getBoundingClientRect();
        if (!rect.width) return false;

        var panel = this.minicart && this.minicart.querySelector('.Minicart');
        var width = panel ? panel.getBoundingClientRect().width : 0;
        var edge = window.innerWidth - (width || DRAWER_WIDTH);

        return rect.right > edge;
      }
    };

    return new StreamshopCartOffset();
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  function bootLiveSections() {
    document.querySelectorAll('[data-streamshop-live]').forEach(function (section) {
      if (section.dataset.streamshopReady) return;
      section.dataset.streamshopReady = 'true';
      StreamshopLiveFactory(section);
    });
  }

  function boot() {
    if (config.cart !== false) {
      // Kept on the namespace so the cart queue is observable (tests, console).
      theme.streamshop.bridge = StreamshopCartBridgeFactory({
        openCartOnAdd: config.openCartOnAdd !== false,
      });
    }

    if (config.widget) {
      StreamshopWidgetFactory({ widget: config.widget, delay: config.delay });
      StreamshopCartOffsetFactory();
    }

    bootLiveSections();

    // Theme editor re-renders sections without a full page load.
    document.addEventListener('shopify:section:load', bootLiveSections);
  }

  theme.streamshop = {
    boot: boot,
    widget: StreamshopWidgetFactory,
    live: StreamshopLiveFactory,
    cartBridge: StreamshopCartBridgeFactory,
    cartOffset: StreamshopCartOffsetFactory,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
